/*
 * INTEX CUSTOM — not part of vanilla Ignite. See INTEX-CHANGES.md §34.
 *
 * <intex-fitment-checker>: the product page's "Does this fit my model?" pill
 * (snippets/intex-fitment-checker.liquid).
 *
 * The shopper's saved models are the header finder's own entries, read through
 * window.intexFinder.readEntries() (assets/intex-finder-cta.js), and a saved
 * model fits when every one of its filters names one of this product's
 * models — the rule assets/compatibility-badge.js fits() applies, so the
 * verdict here and the "Fits" badges on product cards always agree.
 *
 *   saved models   state    pill
 *   none           search   a search field over the models this part fits
 *   (picked one)   picked   "Fits the …", until the shopper saves or searches again
 *   one, fits      fits     "Fits your …"
 *   one, does not  nofit    "Doesn't fit your …"
 *   two or more    multi    "Fits n of your m saved models" + a list
 *
 * Saving only ever happens in the header finder, through its own public
 * methods (open, openForm, read) — this file never writes storage. It
 * re-renders on `compatibility-finder:change` (same tab) and `storage` (other
 * tabs), the two signals the header chip listens to.
 *
 * Every click is stopped here: Benchmark's onDocumentClick closes the finder
 * for any click it sees outside <compatibility-finder> (§9.2), so a click that
 * opened it would otherwise close it again in the same event.
 *
 * Search uses String.prototype.includes, never new RegExp(query) (§1,
 * invariant 2).
 */
if (!customElements.get('intex-fitment-checker')) {
  const STORAGE_KEY = 'theme-compatibility-finder';
  const CHANGE_EVENT = 'compatibility-finder:change';
  const MODEL_PARAM = 'filter.p.m.custom.compat_models';

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  /* One model name in the store carries a literal "&amp;" (§14.40a); show it
     as the ampersand it means. */
  const normalise = value => (value == null ? '' : String(value)).replace(/&amp;/g, '&').trim();
  const fold = value => normalise(value).toLowerCase();
  const joinMeta = parts => parts.map(normalise).filter(Boolean).join(' · ');

  /* "A", "A and B", "A, B and C". */
  const listOf = names => (names.length < 2
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

  const stem = token => (token.length > 3 && token.endsWith('s') ? token.slice(0, -1) : token);

  function savedEntries() {
    return window.intexFinder?.readEntries?.() || [];
  }

  /* The same results URL the header chip and the finder build (§14.32). */
  function resultsUrl(entry) {
    return window.intexFinder?.resultsUrl?.(entry) || '/collections/all-parts';
  }

  /* An entry in the finder's own shape for a model that is not saved, so the
     URL and the finder's preselect treat it exactly like a saved one. */
  function entryFor(model) {
    return {
      id: `cf-${Date.now()}-${Math.round(Math.random() * 1000)}`,
      name: model.name,
      custom: false,
      filters: [{ param: MODEL_PARAM, value: model.id, label: model.name }],
      params: { model_title: model.name },
      image: model.image || undefined,
      subtitle: joinMeta([model.number, model.year]) || undefined
    };
  }

  /* With the header set to hide on scroll, the finder panel lives inside a
     header that may be off screen; bring it back before opening. */
  function revealHeader() {
    const section = document.querySelector('.section-header');
    if (!section?.classList.contains('shopify-section-header-hidden')) return;
    document.querySelector('sticky-header')?.reveal?.();
  }

  /* compatibility-finder.js is a lazy-script, so it may not have run yet.
     Ask for it, show the panel's <details> straight away so the click visibly
     does something, and finish the job once the element is defined. */
  function withFinder(callback) {
    const element = document.querySelector('compatibility-finder');
    if (!element) return;

    revealHeader();

    if (typeof element.open === 'function') {
      callback(element);
      return;
    }

    document
      .querySelectorAll('lazy-script[src*="intex-compat-picker"], lazy-script[src*="compatibility-finder"]')
      .forEach(script => script.loadScript?.());
    element.querySelector('details')?.setAttribute('open', '');
    customElements.whenDefined('compatibility-finder').then(() => callback(element));
  }

  class IntexFitmentChecker extends HTMLElement {
    constructor() {
      super();

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onHostKeyDown = this.onHostKeyDown.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onListPointerDown = this.onListPointerDown.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onSavedChange = this.onSavedChange.bind(this);
      this.onStorage = this.onStorage.bind(this);
    }

    connectedCallback() {
      if (this.isBound) return;
      /* Belt and braces: the snippet's quick-add-hidden wrapper already keeps
         it out of the quick buy modal. */
      if (this.closest('quick-add-modal')) return;

      this.models = this.readModels();
      if (!this.models.length) {
        this.hidden = true;
        return;
      }

      this.isBound = true;
      this.byId = new Map(this.models.map(model => [model.id, model]));
      this.uid = this.dataset.uid || 'IntexFit';
      this.searchUrl = this.dataset.searchUrl || '/search';

      const q = selector => this.querySelector(selector);
      this.pill = q('[data-fit-pill]');
      this.disc = q('[data-fit-disc]');
      this.thumb = q('[data-fit-thumb]');
      this.thumbImg = this.thumb.querySelector('img');
      this.badge = q('[data-fit-badge]');
      this.eyebrow = q('[data-fit-eyebrow]');
      this.input = q('[data-fit-input]');
      /* Not `this.title`: that is HTMLElement's own title attribute. */
      this.titleEl = q('[data-fit-title]');
      this.sub = q('[data-fit-sub]');
      this.ends = {
        search: q('[data-fit-end-search]'),
        fits: q('[data-fit-end-fits]'),
        nofit: q('[data-fit-end-nofit]'),
        toggle: q('[data-fit-toggle]')
      };
      this.panel = q('[data-fit-panel]');
      this.list = q('[data-fit-list]');
      this.searchLink = q('[data-fit-search-link]');
      this.band = q('[data-fit-band]');
      this.saved = q('[data-fit-saved]');
      this.actions = q('[data-fit-actions]');

      /* null = follow the saved models; otherwise
         { mode: 'search' } or { mode: 'picked', model }. */
      this.view = null;
      this.expanded = false;
      this.matches = [];
      this.activeIndex = -1;

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onHostKeyDown);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeyDown);
      this.input.addEventListener('focus', this.onFocus);
      this.list.addEventListener('pointerdown', this.onListPointerDown);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener(CHANGE_EVENT, this.onSavedChange);
      window.addEventListener('storage', this.onStorage);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener(CHANGE_EVENT, this.onSavedChange);
      window.removeEventListener('storage', this.onStorage);
    }

    readModels() {
      try {
        const parsed = JSON.parse(this.querySelector('[data-fit-models]')?.textContent || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed
          .filter(model => model && model.id)
          .map(model => ({
            id: model.id,
            name: normalise(model.name),
            number: normalise(model.number),
            type: normalise(model.type),
            year: normalise(model.year),
            image: normalise(model.image)
          }))
          .sort((a, b) => collator.compare(a.name, b.name));
      } catch (error) {
        return [];
      }
    }

    fits(entry) {
      return entry.filters.length > 0 && entry.filters.every(filter => this.byId.has(filter.value));
    }

    /* The product's own record of a saved model, when the model is one of
       this part's — richer than the entry (number, type). */
    modelOf(entry) {
      const filter = entry.filters.find(candidate => this.byId.has(candidate.value));
      return filter ? this.byId.get(filter.value) : null;
    }

    savedEntryFor(model) {
      return savedEntries().find(entry => entry.filters.some(filter => filter.value === model.id)) || null;
    }

    /* ---------------------------------------------------------------- RENDER */

    render() {
      const entries = savedEntries();
      let view = this.view;

      if (!view) {
        if (!entries.length) view = { mode: 'search' };
        else if (entries.length === 1) view = { mode: this.fits(entries[0]) ? 'fits' : 'nofit', entry: entries[0] };
        else view = { mode: 'multi', entries };
      }

      this.dataset.state = view.mode;
      this.actions.textContent = '';
      this.setBand(null);
      this.saved.hidden = true;
      this.setBadge(0);
      if (view.mode !== 'search') this.closeList();

      switch (view.mode) {
        case 'search': this.renderSearch(); break;
        case 'picked': this.renderPicked(view.model); break;
        case 'fits': this.renderFits(view.entry); break;
        case 'nofit': this.renderNofit(view.entry); break;
        case 'multi': this.renderMulti(view.entries); break;
      }
    }

    setPill({ eyebrow, title, sub, image, end }) {
      const isSearch = this.dataset.state === 'search';

      this.input.hidden = !isSearch;
      this.titleEl.hidden = isSearch;
      if (!isSearch) this.titleEl.textContent = title || '';

      this.eyebrow.hidden = !eyebrow;
      this.eyebrow.textContent = eyebrow || '';
      this.sub.hidden = !sub;
      this.sub.textContent = sub || '';

      if (image) this.thumbImg.src = image;
      this.thumb.hidden = !image;
      this.disc.hidden = Boolean(image);

      Object.entries(this.ends).forEach(([key, element]) => {
        element.hidden = key !== end;
      });
    }

    setBand(kind, parts) {
      this.band.hidden = !kind;
      this.band.className = kind ? `iw-check__band iw-check__band--${kind}` : 'iw-check__band';
      this.band.textContent = '';
      if (!kind) return;

      parts.forEach(part => {
        if (typeof part === 'string') {
          this.band.append(part);
        } else {
          const strong = document.createElement('strong');
          strong.textContent = part.strong;
          this.band.append(strong);
        }
      });
    }

    setBadge(count) {
      this.badge.hidden = !count;
      this.badge.textContent = count ? String(count) : '';
    }

    renderSearch() {
      this.setPill({
        eyebrow: 'Check fit',
        sub: 'Only models this part fits are suggested',
        end: 'search'
      });
      this.input.setAttribute('aria-expanded', String(!this.panel.hidden));

      this.addHelper('Only models this part fits are suggested.');
      this.addButton('Or save your model in the Parts Finder', 'add', { chevron: true });
    }

    renderPicked(model) {
      this.setPill({
        eyebrow: 'Fits the',
        title: model.name,
        sub: joinMeta([model.number, model.type, model.year]),
        image: model.image,
        end: 'fits'
      });
      this.setBand('fits', ['Confirmed fit for the ', { strong: model.name }, ' — you\'re good to order.']);

      this.addLink(`See all parts for ${model.name}`, resultsUrl(entryFor(model)), { chevron: true });
      this.addButton('Search again', 'search-again', { muted: true });
      this.addButton('Save it in the Parts Finder to see \'Fits\' badges store-wide', 'save', { muted: true });
    }

    renderFits(entry) {
      const model = this.modelOf(entry);
      const name = normalise(entry.name) || model?.name || '';

      this.setPill({
        eyebrow: 'Fits your',
        title: name,
        sub: model ? joinMeta([model.number, model.type, model.year]) : normalise(entry.subtitle),
        image: normalise(entry.image) || model?.image,
        end: 'fits'
      });
      this.setBand('fits', ['This part fits your ', { strong: name }, ' — you\'re good to order.']);

      this.addLink(`See all parts for ${name}`, resultsUrl(entry), { chevron: true });
      this.addButton('Not your model? Change it', 'change', { muted: true });
    }

    renderNofit(entry) {
      const name = normalise(entry.name);

      this.setPill({
        eyebrow: 'Doesn\'t fit your',
        title: name,
        sub: normalise(entry.subtitle),
        image: normalise(entry.image),
        end: 'nofit'
      });
      /* No list of the models it does fit (§14.102): on a part that fits
         130 models, three names and "128 more" told the shopper nothing.
         The way on is the header finder, through "Change it" (§14.103). */
      this.setBand('nofit', [
        'This part doesn\'t fit your ', { strong: name },
        '. Check the model number on your product, or change your saved model to see what it fits.'
      ]);

      this.addLink(`See parts that fit ${name}`, resultsUrl(entry), { primary: true });
      this.addButton('Not your model? Change it', 'change', { muted: true });
    }

    renderMulti(entries) {
      const fitting = entries.filter(entry => this.fits(entry));
      const anyFit = fitting.length > 0;

      this.setPill({
        title: anyFit
          ? `Fits ${fitting.length} of your ${entries.length} saved models`
          : 'Doesn\'t fit your saved models',
        sub: listOf(entries.map(entry => normalise(entry.name))),
        end: anyFit ? 'toggle' : 'nofit'
      });
      this.setBadge(entries.length);

      this.renderSaved(entries);
      this.saved.hidden = anyFit && !this.expanded;
      this.ends.toggle.setAttribute('aria-expanded', String(anyFit && this.expanded));
      this.ends.toggle.setAttribute('aria-label', this.expanded ? 'Hide your saved models' : 'Show your saved models');

      /* With several models saved there is no one model to link to, so the
         row is only the way into the finder (§14.98). */
      this.addButton('Manage saved models', 'manage', { muted: true });
    }

    renderSaved(entries) {
      this.saved.textContent = '';

      entries.forEach(entry => {
        const fits = this.fits(entry);
        const model = this.modelOf(entry);

        const row = document.createElement('li');
        row.className = `iw-check__row iw-check__row--${fits ? 'fits' : 'nofit'}`;

        row.append(this.buildThumb(normalise(entry.image) || model?.image));

        const text = document.createElement('span');
        text.className = 'iw-check__text';
        const name = document.createElement('span');
        name.className = 'iw-check__name';
        name.textContent = normalise(entry.name);
        const meta = document.createElement('span');
        meta.className = 'iw-check__meta';
        meta.textContent = model ? joinMeta([model.number, model.year]) : normalise(entry.subtitle);
        text.append(name, meta);
        row.append(text);

        const chip = document.createElement('span');
        chip.className = `iw-check__chip iw-check__chip--${fits ? 'fits' : 'nofit'}`;
        const icon = this.icon(fits ? 'check' : 'x');
        if (icon) chip.append(icon);
        chip.append(fits ? 'Fits' : 'Doesn\'t fit');
        row.append(chip);

        this.saved.append(row);
      });
    }

    icon(name) {
      const template = this.querySelector(`template[data-fit-icon="${name}"]`);
      return template?.content.firstElementChild?.cloneNode(true) || null;
    }

    buildThumb(image) {
      const thumb = document.createElement('span');
      thumb.className = 'iw-check__thumb';
      thumb.setAttribute('aria-hidden', 'true');

      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.width = 36;
        img.height = 36;
        img.loading = 'lazy';
        thumb.append(img);
      } else {
        const icon = this.icon('gear');
        if (icon) thumb.append(icon);
      }
      return thumb;
    }

    addHelper(text) {
      const span = document.createElement('span');
      span.className = 'iw-check__helper';
      span.textContent = text;
      this.actions.append(span);
    }

    decorate(element, label, { chevron, muted } = {}) {
      element.append(label);
      if (chevron) {
        const mark = document.createElement('span');
        mark.setAttribute('aria-hidden', 'true');
        mark.textContent = ' ›';
        element.append(mark);
      }
      if (muted) element.classList.add('iw-check__link--muted');
    }

    addLink(label, href, options = {}) {
      const link = document.createElement('a');
      link.className = options.primary ? 'iw-check__button' : 'iw-check__link';
      link.href = href;
      this.decorate(link, label, options);
      this.actions.append(link);
    }

    addButton(label, action, options = {}) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'iw-check__link';
      button.dataset.fitAction = action;
      this.decorate(button, label, options);
      this.actions.append(button);
    }

    /* -------------------------------------------------------------- SEARCH */

    score(model, query) {
      const number = fold(model.number);
      const name = fold(model.name);
      if (number && number === query) return 1000;
      if (name === query) return 500;

      const tokens = query.split(/\s+/).filter(Boolean).map(stem);
      let total = 0;
      for (const token of tokens) {
        let points = 0;
        if (number.startsWith(token)) points = 800;
        else if (number.includes(token)) points = 600;
        else if (name.startsWith(token)) points = 300;
        else if (name.includes(token)) points = 200;
        if (!points) return 0;
        total += points;
      }
      return total;
    }

    /* Empty query: every model this part fits, A-Z. Otherwise the scored
       matches, best first, ties A-Z. */
    match(raw) {
      const query = fold(raw);
      if (!query) return this.models.slice();

      return this.models
        .map(model => ({ model, score: this.score(model, query) }))
        .filter(result => result.score > 0)
        .sort((a, b) => b.score - a.score || collator.compare(a.model.name, b.model.name))
        .map(result => result.model);
    }

    openList() {
      this.matches = this.match(this.input.value);
      this.activeIndex = -1;
      this.renderList();
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeList() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    renderList() {
      this.list.textContent = '';

      this.matches.forEach((model, index) => {
        const option = document.createElement('li');
        option.className = 'iw-check__option';
        option.id = `${this.uid}-option-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.index = String(index);

        option.append(this.buildThumb(model.image));

        const text = document.createElement('span');
        text.className = 'iw-check__text';
        const name = document.createElement('span');
        name.className = 'iw-check__name';
        name.textContent = model.name;
        if (model.number) {
          const number = document.createElement('span');
          number.className = 'iw-check__number';
          number.textContent = model.number;
          name.append(number);
        }
        text.append(name);

        const metaText = joinMeta([model.type, model.year]);
        if (metaText) {
          const meta = document.createElement('span');
          meta.className = 'iw-check__meta';
          meta.textContent = metaText;
          text.append(meta);
        }
        option.append(text);

        this.list.append(option);
      });

      this.list.hidden = this.matches.length === 0;

      const query = normalise(this.input.value);
      this.searchLink.hidden = !query;
      if (query) {
        const url = new URL(this.searchUrl, window.location.origin);
        url.searchParams.set('q', query);
        this.searchLink.href = url.toString();
        this.searchLink.textContent = `Search all parts for '${query}' ›`;
      }
    }

    setActive(index) {
      const options = this.list.querySelectorAll('.iw-check__option');
      this.activeIndex = index;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));

      if (index >= 0 && options[index]) {
        this.input.setAttribute('aria-activedescendant', options[index].id);
        options[index].scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    choose(model) {
      if (!model) return;

      /* Picking a model the shopper already has saved shows the saved
         verdict, not a second "Fits the" for the same thing. */
      this.view = this.savedEntryFor(model) ? null : { mode: 'picked', model };
      this.input.value = '';
      this.closeList();
      this.render();

      /* The input is hidden now, so hand focus to the links row: "Search
         again" for a picked model, otherwise its first control. */
      (this.actions.querySelector('[data-fit-action="search-again"]')
        || this.actions.querySelector('a, button'))?.focus();
    }

    /* Picked state -> search, with the field focused and the list open. */
    searchAgain() {
      this.view = { mode: 'search' };
      this.input.value = '';
      this.render();
      this.input.focus();
      if (this.panel.hidden) this.openList();
    }

    /* -------------------------------------------------------------- EVENTS */

    onFocus() {
      if (this.panel.hidden) this.openList();
    }

    onInput() {
      this.openList();
    }

    onKeyDown(event) {
      const isOpen = !this.panel.hidden;
      const count = this.matches.length;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) this.openList();
          if (this.matches.length) this.setActive((this.activeIndex + 1) % this.matches.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) this.openList();
          if (this.matches.length) this.setActive(this.activeIndex <= 0 ? this.matches.length - 1 : this.activeIndex - 1);
          break;
        case 'Enter': {
          event.preventDefault();
          if (this.activeIndex >= 0) {
            this.choose(this.matches[this.activeIndex]);
          } else if (count === 1) {
            this.choose(this.matches[0]);
          } else {
            const query = fold(this.input.value);
            this.choose(this.models.find(model => query && (fold(model.number) === query || fold(model.name) === query)));
          }
          break;
        }
        case 'Escape':
          /* stopPropagation so Benchmark's Escape handler does not also act. */
          event.stopPropagation();
          if (isOpen) {
            event.preventDefault();
            this.closeList();
          } else if (this.input.value) {
            this.input.value = '';
          }
          break;
        case 'Tab':
          this.closeList();
          break;
      }
    }

    /* Escape anywhere in the widget while a picked model shows goes back to
       search, the same as "Search again". The input's own handler covers
       Escape in search mode and stops it there. */
    onHostKeyDown(event) {
      if (event.key !== 'Escape' || this.dataset.state !== 'picked') return;
      event.preventDefault();
      event.stopPropagation();
      this.searchAgain();
    }

    /* Keeps focus in the input, so its blur cannot close the list before the
       click on an option lands. */
    onListPointerDown(event) {
      event.preventDefault();
    }

    onDocumentPointerDown(event) {
      if (this.panel.hidden) return;
      if (this.querySelector('.iw-check__field').contains(event.target)) return;
      this.closeList();
    }

    onClick(event) {
      event.stopPropagation();

      const option = event.target.closest('.iw-check__option');
      if (option) {
        this.choose(this.matches[Number(option.dataset.index)]);
        return;
      }

      if (event.target.closest('[data-fit-toggle]')) {
        this.expanded = !this.expanded;
        this.render();
        this.ends.toggle.focus();
        return;
      }

      const action = event.target.closest('[data-fit-action]')?.dataset.fitAction;
      if (action) {
        this.runAction(action);
        return;
      }

      /* The rest of the pill (§14.103). Search: puts the caret in the field.
         Picked: back to search. Several saved: the caret's toggle, when it is
         showing. A saved single-model verdict does nothing. Keyboard users
         have the real controls for each. */
      if (!this.pill.contains(event.target)) return;

      switch (this.dataset.state) {
        case 'search':
          if (event.target !== this.input) this.input.focus();
          if (this.panel.hidden) this.openList();
          break;
        case 'picked':
          this.searchAgain();
          break;
        case 'multi':
          if (!this.ends.toggle.hidden) {
            this.expanded = !this.expanded;
            this.render();
          }
          break;
      }
    }

    runAction(action) {
      if (action === 'search-again') {
        this.searchAgain();
        return;
      }

      const view = this.view;
      const entries = savedEntries();

      withFinder(finder => {
        switch (action) {
          case 'add':
            finder.open();
            if (finder.read().length < finder.limit) finder.openForm(null);
            break;

          case 'save': {
            /* Preselect the picked model in the finder's form (its picker
               reads the model GID back out of the entry's filters), and say
               "Save model" while we are the ones who opened it. A full finder
               cannot take another entry — write() would slice the new one
               off — so that case lands on the saved list instead. */
            const model = view?.mode === 'picked' ? view.model : null;
            if (!model) {
              finder.open();
              break;
            }
            const existing = finder.read().find(entry => entry.filters.some(filter => filter.value === model.id));
            finder.open();
            if (!existing && finder.read().length >= finder.limit) break;
            finder.openForm(existing || entryFor(model));
            if (finder.saveLabel) finder.saveLabel.textContent = 'Save model';
            break;
          }

          case 'change': {
            const entry = entries.length === 1 ? entries[0] : null;
            const current = entry ? finder.read().find(candidate => candidate.id === entry.id) : null;
            finder.open();
            if (current) finder.openForm(current);
            break;
          }

          case 'manage':
          default:
            finder.open();
        }
      });
    }

    onSavedChange() {
      this.view = null;
      this.expanded = false;
      this.render();
    }

    onStorage(event) {
      if (event.key === null || event.key === STORAGE_KEY) this.onSavedChange();
    }
  }

  customElements.define('intex-fitment-checker', IntexFitmentChecker);
}